import { pickBy } from 'lodash';
import { toInteger } from 'lodash';
import castArray from 'lodash/castArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import map from 'lodash/map';
import noop from 'lodash/noop';
import toFinite from 'lodash/toFinite';
import { DateTime } from 'luxon';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { usePrevious } from 'react-use';
import { useUpdateEffect } from 'react-use';
import { useDebounce } from 'use-debounce';
import mskMoment from '../../../api/utils/mskMoment';
import { initialEmptyState } from '../Purchases.type';
import { PurchasesState } from '../Purchases.type';
import { useLoadingIndicator } from './useLoadingIndicator';

export type QueryParams = Record<
    keyof PurchasesState,
    string | string[] | undefined
>;

const encodeDate = (value: DateTime | null) => (value ? value.toISO() : '');
const encodeNumber = (value: number | null) =>
    isNumber(value) ? String(value) : '';

const encodeStateParams = (state: PurchasesState): QueryParams => ({
    search: state.search || '',
    region: map(state.region, (value) => value.regionCode),
    okpd2: map(state.okpd2, (value) => value.code),
    section: map(state.section, (value) => String(value.name)),
    tariff: map(state.tariff, (value) => String(value.id)),
    block: map(state.block, (value) => String(value.id)),
    procedureTemplate: map(state.procedureTemplate, (value) =>
        String(value.id)
    ),
    publishedFrom: encodeDate(state.publishedFrom),
    publishedTo: encodeDate(state.publishedTo),
    receiveStartFrom: encodeDate(state.receiveStartFrom),
    receiveStartTo: encodeDate(state.receiveStartTo),
    receiveFinishFrom: encodeDate(state.receiveFinishFrom),
    receiveFinishTo: encodeDate(state.receiveFinishTo),
    startPriceFrom: encodeNumber(state.startPriceFrom),
    startPriceTo: encodeNumber(state.startPriceTo),
    procedure: map(state.procedure, (value) => String(value.id)),
    freeParticipation: String(toFinite(state.freeParticipation)),
    sectionOrg: String(toFinite(state.sectionOrg)) || undefined,
    sortBy: state.sortBy || 'publish_date',
    sortOrder: String(state.sortOrder || -1),
    page: String(state.page || 1),
});

const emptyQueryParams = encodeStateParams({} as PurchasesState);

const toString = (value: string | string[] | undefined): string =>
    (Array.isArray(value) ? value[0] : value) || '';

const toNumber = (value: string | string[] | undefined): number | null => {
    const stringValue = Array.isArray(value) ? value[0] : value;
    if (stringValue) {
        return toFinite(stringValue);
    }
    return null;
};

export const toBoolean = (value: string | string[] | undefined) =>
    ['1', 'true'].includes(toString(value));

const toDateTime = (value: string | string[] | undefined) =>
    value ? mskMoment(toString(value)) : null;

function toCollection<T extends { [p in K]: U }, K extends string, U>(
    value: string | string[] | undefined,
    idField: K,
    format: (value: string) => U
): T[] {
    if (!value) {
        return [];
    }
    return castArray(value)
        .filter(Boolean)
        .map((id) => ({ [idField]: format(id) } as T));
}

const decodeStateParams = (query: QueryParams): PurchasesState => ({
    search: toString(query.search),
    region: toCollection(query.region, 'regionCode', String),
    okpd2: toCollection(query.okpd2, 'code', String),
    section: toCollection(query.section, 'name', String),
    tariff: toCollection(query.tariff, 'id', Number),
    block: toCollection(query.block, 'id', Number),
    procedureTemplate: toCollection(query.procedureTemplate, 'id', Number),
    publishedFrom: toDateTime(query.publishedFrom) || null,
    publishedTo: toDateTime(query.publishedTo) || null,
    receiveStartFrom: toDateTime(query.receiveStartFrom) || null,
    receiveStartTo: toDateTime(query.receiveStartTo) || null,
    receiveFinishFrom: toDateTime(query.receiveFinishFrom) || null,
    receiveFinishTo: toDateTime(query.receiveFinishTo) || null,
    startPriceFrom: toNumber(query.startPriceFrom),
    startPriceTo: toNumber(query.startPriceTo),
    procedure: toCollection(query.procedure, 'id', Number),
    freeParticipation: toBoolean(query.freeParticipation),
    sectionOrg: toFinite(query.sectionOrg) || null,
    sortBy: toString(query.sortBy) || 'publish_date',
    sortOrder: toInteger(query.sortOrder || -1),
    page: Math.max(toInteger(query.page), 1),
});

interface UseQueryParams {
    state: PurchasesState;
    submitCounter: number;
    autoScroll?: boolean;
    resetForm?: () => void;
}

/**
 * Синхронизация состояния фильтра с параметрами роутера
 */
export const useQueryParams = ({
    state,
    submitCounter,
    autoScroll = false,
    resetForm,
}: UseQueryParams) => {
    const router = useRouter();

    /*
     * Используем отложенное состояние фильтра, чтобы сократить количество запросов к серверу
     */
    const [debouncedState, { isPending }] = useDebounce(state, 500, {
        leading: false,
        trailing: true,
        maxWait: 2000,
    });

    const prevSubmitCounter = usePrevious(submitCounter);

    /*
     * Обновляем параметры роутера если они отличаются от параметров фильтра
     */
    useUpdateEffect(() => {
        if (router.isReady) {
            const actualEncodedState = encodeStateParams(debouncedState);
            const decodedState = decodeStateParams(router.query as QueryParams);
            const currentEncodedState = {
                ...actualEncodedState,
                ...encodeStateParams(decodedState),
            };
            if (
                !isEqual(currentEncodedState, actualEncodedState) ||
                submitCounter !== prevSubmitCounter
            ) {
                const changedQueryParams = pickBy(
                    actualEncodedState,
                    (value, key) =>
                        !isEqual(
                            value,
                            emptyQueryParams[key as keyof QueryParams]
                        )
                );
                const href = {
                    pathname: router.pathname,
                    query: changedQueryParams,
                };
                router.push(href, href, { scroll: autoScroll }).catch(noop);
            }
        }
    }, [debouncedState, router.isReady, submitCounter]);

    useEffect(() => {
        if (isEmpty(router.query)) {
            resetForm?.();
        }
    }, [resetForm, router.query]);

    return useLoadingIndicator(isPending, [state, debouncedState]);
};

export const useSyncQueryWithFilter = (
    setValues: (state: PurchasesState) => void
) => {
    const router = useRouter();
    const decodedState = decodeStateParams(router.query as QueryParams);

    useUpdateEffect(() => {
        setValues(decodedState);
    }, [router.query]);
};

export const useInitialState = () => {
    const router = useRouter();
    return useMemo(
        () => {
            const initialState = decodeStateParams(router.query as QueryParams);
            const emptyFilterState: PurchasesState = {
                ...initialEmptyState,
            };
            return { initialState, emptyFilterState };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
};
